import { isUndefinedOrEmpty } from '@afterpay/utils/string'
import { isValidAddressComponent, isValidAddressInput, isValidEmail, valueIsNotNullOrUndefined, isValidName } from './validation'
import { isValidPostcode } from './postcode-validations'
import { CountryCode } from 'libphonenumber-js'
import { ConsumerCheckoutAddress } from '@afterpay/types'
import { AllCountryCode, InputAddressData } from '@afterpaytouch/core'
import { isCallable } from '@afterpay/utils'
import { TFunction } from 'next-i18next'

export interface AddressValidationConfig {
  state: boolean
  postcode: boolean
  suburb: boolean
}

export const requiredAddressFieldsByCountry: Record<CountryCode | string, AddressValidationConfig> = {
  AU: {
    state: true,
    postcode: true,
    suburb: true,
  },
  NZ: {
    state: true,
    postcode: true,
    suburb: true,
  },
  US: {
    state: true,
    postcode: true,
    suburb: true,
  },
  CA: {
    state: true,
    postcode: true,
    suburb: true,
  },
  GB: {
    state: false,
    postcode: true,
    suburb: true,
  },
  IM: {
    state: false,
    postcode: true,
    suburb: true,
  },
  JE: {
    state: false,
    postcode: true,
    suburb: true,
  },
  GG: {
    state: false,
    postcode: true,
    suburb: true,
  },
  FR: {
    state: false,
    postcode: true,
    suburb: true,
  },
  ES: {
    state: false,
    postcode: true,
    suburb: true,
  },
  IT: {
    state: false,
    postcode: true,
    suburb: true,
  },
  DEFAULT: {
    state: false,
    postcode: false,
    suburb: true,
  },
}

export const requiredFormFields = {
  FR: {
    fiscalIdentification: false,
    nationality: true,
  },
  ES: {
    fiscalIdentification: true,
    nationality: false,
  },
  IT: {
    fiscalIdentification: true,
    nationality: true,
  },
  DEFAULT: {
    fiscalIdentification: false,
    nationality: false,
  },
}

export const validateProfileField = (country, key, value, isMobileLogin = false, isNie = false): boolean => {
  const requiredFields = requiredAddressFieldsByCountry[country] ?? requiredAddressFieldsByCountry.DEFAULT
  const formFields = requiredFormFields[country] ?? requiredFormFields.DEFAULT

  const validate = {
    givenNames: (name) => isValidName({ name }),
    surname: (name) => isValidName({ name }),
    otherGivenNames: (name) => isValidName({ name, optional: true }),
    dateOfBirth: (input) => !isUndefinedOrEmpty(input),
    phoneNumber: (input) => !isUndefinedOrEmpty(input),
    address1: (input) => isValidAddressComponent({ input }),
    address2: (input) => isValidAddressComponent({ input, optional: true }),
    area2: (input) => isValidAddressComponent({ input, optional: true }),
    countryCode: (input) => !isUndefinedOrEmpty(input),
    postcode: (input) => isValidPostcode(country, input, !requiredFields.postcode),
    state: (input) => isValidAddressComponent({ input, optional: !requiredFields.state }),
    suburb: (input) => isValidAddressComponent({ input, optional: !requiredFields.suburb }),
    email: (input) => (isMobileLogin ? isValidEmail(input) : true),
    creditCheckAgree: (input) => valueIsNotNullOrUndefined(input),
    fiscalIdentification: (input) => (formFields.fiscalIdentification ? !isUndefinedOrEmpty(input) : true),
    nationality: (input) => (formFields.nationality || isNie ? !isUndefinedOrEmpty(input) : true),
    addressType: () => true,
    id: () => true,
  }

  return isCallable(validate[key]) && validate[key](value)
}

// Returns which single postcode gap (if any) is the ONLY thing failing GB address
// validation: 'incomplete' (outward-only code like "M24"), 'missing' (blank), or null
// (postcode is fine, or other fields are also invalid). Lets the address forms show a
// postcode-specific prompt instead of the generic "select a valid address".
export const gbPostcodeGap = (address: InputAddressData, country: AllCountryCode): 'missing' | 'incomplete' | null => {
  // Callers (e.g. the edit-address modal's custom validation) can invoke this before an
  // address object exists, so bail rather than dereference a non-object.
  if (address === null || typeof address !== 'object') {
    return null
  }
  if (validateProfileField(country, 'postcode', address.postcode)) {
    return null
  }
  // Only the address-component fields — not arbitrary keys. Callers can pass a richer
  // object (e.g. ConsumerAddress with name/id) whose extra keys validateProfileField has
  // no handler for, which would otherwise read as a failure and suppress the prompt.
  const addressFields: Array<keyof InputAddressData> = ['address1', 'address2', 'suburb', 'state', 'area2', 'countryCode']
  const restOfAddressValid = addressFields.every((key) => validateProfileField(country, key, address[key]))
  if (!restOfAddressValid) {
    return null
  }
  return typeof address.postcode === 'undefined' || address.postcode.trim().length === 0 ? 'missing' : 'incomplete'
}

// GB-only: the specific address-gap error message (missing street, or incomplete/missing postcode),
// or null when there's no single gap. Shared by the portal's GB address forms (ProfileForm,
// EditAddressModal) so the prompt logic isn't copy-pasted per form. Check this before the generic
// validAddressOption, which accepts an outward-only postcode and would otherwise mask the gap.
export const gbAddressGapError = (address: InputAddressData, country: AllCountryCode, t: TFunction): string | null => {
  if (country !== 'GB') {
    return null
  }
  if (typeof address.address1 !== 'string' || address.address1.trim().length === 0) {
    return t('signUp:error:missingStreet')
  }
  const gap = gbPostcodeGap(address, country)
  if (gap === 'incomplete') {
    return t('signUp:error:incompletePostcode')
  }
  if (gap === 'missing') {
    return t('signUp:error:missingPostcode')
  }
  return null
}

export const isValidAddress = (address: ConsumerCheckoutAddress): boolean => {
  const requiredFields = requiredAddressFieldsByCountry[address?.countryCode] ?? requiredAddressFieldsByCountry.DEFAULT

  return (
    isValidAddressInput(address?.address1) &&
    isValidPostcode(address?.countryCode, address?.postcode, !requiredFields.postcode) &&
    isValidAddressInput(address?.state, !requiredFields.state) &&
    isValidAddressInput(address?.suburb, !requiredFields.suburb)
  )
}

export const getAddressOptionalFields = (country): string[] => {
  const requiredFields = requiredAddressFieldsByCountry[country] ?? requiredAddressFieldsByCountry.DEFAULT
  return Object.keys(requiredFields).filter((key) => requiredFields[key] === false)
}
